import { NewCondition } from 'types/Conditions';
import { SingleConditionData } from 'services/conditions/useConditionMetrics';

import { Card, Typography } from 'components';

import styles from './ConditionMetricsAssociated.module.scss';

import { ConditionMetricRow } from './ConditionMetricRow';
import { ConditionMetricPill } from './ConditionMetricPill';
import { useMemo } from 'react';

export const ConditionMetricsAssociated = ({
  condition,
  conditionData,
}: {
  condition: NewCondition;
  conditionData: SingleConditionData | null;
}) => {
  const colorOrder = ['red', 'orange', 'grey', 'green'];

  const sortedMetrics = useMemo(() => {
    if (!conditionData?.metrics) return [];
    return [...conditionData.metrics].sort((a, b) => {
      const indexA = colorOrder.indexOf(a.evaluation_color);
      const indexB = colorOrder.indexOf(b.evaluation_color);
      return indexA - indexB;
    });
  }, [conditionData]);

  const counts = useMemo(() => {
    if (!conditionData?.metrics)
      return { good: 0, okay: 0, improve: 0, support: 0 };

    return conditionData.metrics.reduce(
      (acc, metric) => {
        if (metric.evaluation_color === 'green') acc.good += 1;
        else if (metric.evaluation_color === 'grey') acc.okay += 1;
        else if (metric.evaluation_color === 'orange') acc.improve += 1;
        else if (metric.evaluation_color === 'red') acc.support += 1;
        return acc;
      },
      { good: 0, okay: 0, improve: 0, support: 0 },
    );
  }, [conditionData]);

  const sum = counts.good + counts.okay + counts.improve + counts.support;

  if (!conditionData) return null;

  return (
    <Card className={styles.cardMetricsContainer} variant='wrapper'>
      <div className={styles.header}>
        <Typography variant='body-s' className={styles.topText}>
          Based on your sample
        </Typography>
        <Typography variant='heading-xl' className={styles.title}>
          {condition.condition_display_name}
        </Typography>
        <ConditionMetricPill
          good={counts.good}
          okay={counts.okay}
          improve={counts.improve}
          support={counts.support}
        />
      </div>
      <div className={styles.content}>
        <Typography variant='label' className={styles.label}>
          {sum} metrics associated with {condition.condition_display_name}
        </Typography>
      </div>
      {sortedMetrics.map((metric, index) => (
        <ConditionMetricRow key={index} metric={metric} row={index} />
      ))}
    </Card>
  );
};
